import { mapState, mapGetters } from 'vuex'
import { PermissionName } from '~/store/usman'

export default {
  computed: {
    ...mapState('usman', [
      'isUseUsmanPermissions',
      'isLoading',
      'isInitialized',
    ]),
    ...mapGetters('usman', [
      'isHasPermission',
      'isLimitedAccessRole',
      'isFullAccessRole',
    ]),
    ...mapState('unified_app', ['isUnifiedApp']),
    ...mapState('preferences', ['billing_info']),
    ...mapState('layouts', ['appConfig']),

    /**
     * Unified menu access control following simplified rules
     * Returns: true (allowed), false (denied), or null (use legacy logic)
     */

    // 🔴 CHANNEL INTEGRATION MENU
    canAccessChannelIntegration() {
      // Wait for initialization from API before making decisions
      if (!this.isInitialized) {
        return false
      }

      if (this.isUseUsmanPermissions) {
        if (this.isLoading) return false

        // Agent/Member: Always denied (even with USMAN true)
        if (this.isLimitedAccessRole) return false

        // Owner/Supervisor/Admin: Check permission
        return this.isHasPermission(PermissionName.settings_integrations_view)
      }

      // USMAN False: Agent/Member denied, others allowed
      return !this.isLimitedAccessRole
    },

    // 🤖 CHATBOT MENU
    canAccessChatbot() {
      // Wait for initialization from API before making decisions
      if (!this.isInitialized) {
        return false
      }

      if (this.isUseUsmanPermissions) {
        if (this.isLoading) return false

        // All roles check permission
        return this.isHasPermission(PermissionName.bot_general_view)
      }

      // USMAN False: Agent/Member denied, others allowed
      return !this.isLimitedAccessRole
    },

    // 📊 DASHBOARD MENU
    canAccessDashboard() {
      // Wait for initialization from API before making decisions
      if (!this.isInitialized) {
        return false
      }

      if (this.isUseUsmanPermissions) {
        if (this.isLoading) return false

        // All roles check permission
        return this.isHasPermission(PermissionName.dashboard_omnichannel_view)
      }

      // USMAN False: Return null to let legacy logic handle (isSlaDashboardActive for agent/member)
      return null
    },

    // 📈 REPORTS MENU
    canAccessReports() {
      // Wait for initialization from API before making decisions
      if (!this.isInitialized) {
        return false
      }

      if (this.isUseUsmanPermissions) {
        if (this.isLoading) return false

        // Agent/Member: Always denied
        if (this.isLimitedAccessRole) return false

        // Owner/Supervisor/Admin: Check permission
        return this.isHasPermission(PermissionName.report_omnichannel_view)
      }

      // USMAN False: Agent/Member denied, others allowed
      return !this.isLimitedAccessRole
    },

    // ⚙️ SETTINGS MENU
    canAccessSettings() {
      // Wait for initialization from API before making decisions
      if (!this.isInitialized) {
        return false
      }

      if (this.isUseUsmanPermissions) {
        if (this.isLoading) return false

        // Agent/Member: Always denied
        if (this.isLimitedAccessRole) return false

        // Owner/Supervisor/Admin: Check permission
        return this.isHasPermission(PermissionName.settings_general_view)
      }

      // USMAN False: Agent/Member denied, others allowed
      return !this.isLimitedAccessRole
    },

    // 📢 CAMPAIGN MENU
    canAccessCampaign() {
      // Wait for initialization from API before making decisions
      if (!this.isInitialized) {
        return false
      }

      if (this.isUseUsmanPermissions) {
        if (this.isLoading) return false

        // All roles check permission
        return this.isHasPermission(PermissionName.campaign_general_view)
      }

      // USMAN False: Return null to let legacy logic handle (isAgentCanSendBroadcast for agent/member)
      return null
    },

    // 📚 RESOURCES MENU (AI Resources/Ask Airene)
    canAccessResources() {
      // Both USMAN states: Return null to let legacy logic handle (isAskAireneActive)
      return null
    },

    // 🔧 CUSTOM SOLUTIONS MENU
    canAccessCustomSolutions() {
      // Both USMAN states: Return null to let legacy logic handle (isCustomSolutionsActive)
      return null
    },

    // 📋 SCORECARD SUBMENU (Settings/Scorecard)
    canAccessScorecard() {
      // Wait for initialization from API before making decisions
      if (!this.isInitialized) {
        return false
      }

      if (this.isUseUsmanPermissions) {
        if (this.isLoading) return false

        // Agent/Member: Always denied
        if (this.isLimitedAccessRole) return false

        // Owner/Supervisor/Admin: Check permission
        return this.isHasPermission(PermissionName.settings_scorecard_view)
      }

      // USMAN False: Agent/Member denied, others allowed
      return !this.isLimitedAccessRole
    },

    // 📞 INTEGRATIONS SUBMENU (Settings/Integrations)
    canAccessIntegrations() {
      // Reuse Channel Integration logic
      return this.canAccessChannelIntegration
    },

    // 💳 FINANCES > PACKAGE USAGES PAGE
    canAccessFinancesPackageUsages() {
      if (!this.isInitialized) {
        return false
      }
      // Agent/Member: Always denied
      if (this.isLimitedAccessRole) {
        return false
      }
      // If is_centralized_package = true → deny access
      if (this.appConfig?.is_centralized_package) {
        return false
      }
      // Otherwise allow access for Owner/Admin/Supervisor
      return true
    },

    // 💳 FINANCES > SUBSCRIPTIONS PAGE
    canAccessSubscriptions() {
      if (!this.isInitialized) {
        return false
      }
      // Agent/Member: Always denied
      if (this.isLimitedAccessRole) {
        return false
      }
      // Rule 1: is_centralized_package = true AND isUnifiedApp = true → allow
      if (this.appConfig?.is_centralized_package && this.isUnifiedApp) {
        return true
      }
      // Rule 2: is_centralized_package = true AND isUnifiedApp = false AND is_qontak_one_package_ui = true → allow
      if (
        this.appConfig?.is_centralized_package &&
        !this.isUnifiedApp &&
        this.billing_info?.is_qontak_one_package_ui
      ) {
        return true
      }

      // Otherwise deny
      return false
    },

    // Check if permissions are still loading
    isPermissionsLoading() {
      // Loading if not initialized or if USMAN is enabled and loading
      return (
        !this.isInitialized || (this.isUseUsmanPermissions && this.isLoading)
      )
    },
  },

  methods: {
    /**
     * Universal menu access checker
     * @param {string} menuType - Type of menu to check
     * @returns {boolean} - Whether user has access
     */
    hasMenuAccess(menuType) {
      const accessMap = {
        'channel-integration': this.canAccessChannelIntegration,
        chatbot: this.canAccessChatbot,
        dashboard: this.canAccessDashboard,
        reports: this.canAccessReports,
        settings: this.canAccessSettings,
        campaign: this.canAccessCampaign,
        resources: this.canAccessResources,
        'custom-solutions': this.canAccessCustomSolutions,
        scorecard: this.canAccessScorecard,
        integrations: this.canAccessIntegrations,
      }

      const access = accessMap[menuType]

      // Only allow access if explicitly granted (true)
      // Deny if explicitly denied (false) or if no permission defined (null/undefined)
      return access === true
    },

    /**
     * Method to check permissions and redirect if necessary
     * @param {string} requiredPermission - Permission to check
     * @returns {Promise<boolean>} - Whether user has access
     */
    async checkPermissionsAndRedirect(requiredPermission) {
      // Wait for permissions to load if using Usman
      if (this.isUseUsmanPermissions && this.isLoading) {
        try {
          await this.$store.dispatch('usman/fetchUsmanConfig')
        } catch (error) {
          // If fetching fails, redirect to safe page
          this.$router.push('/inbox')
          return false
        }
      }

      const hasAccess = this.hasMenuAccess(requiredPermission)

      if (!hasAccess) {
        // Redirect to inbox or appropriate page
        this.$router.push('/inbox')
        return false
      }

      return hasAccess
    },

    /**
     * Legacy permission helpers - maintained for backward compatibility
     */

    // For situations where USMAN returns null and we need legacy behavior
    shouldUseLegacyDashboardLogic() {
      return !this.isUseUsmanPermissions || this.canAccessDashboard === null
    },

    shouldUseLegacyCampaignLogic() {
      return !this.isUseUsmanPermissions || this.canAccessCampaign === null
    },
  },
}
